import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbDate, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from '../../../shared/services/datepicker-adapter';
import { AuthService } from '../../../shared/services/auth.service';
import { FeatureConfigService } from '../../../shared/services/feature-config.service';
import { CampaignMainFeatureConfig, FeatureTypes, User } from '../../../shared/interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '../../../shared/services/user.service';
import { ProjectV2ServiceService } from '../../../shared/services/project-v2-service.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { WorkspaceService } from '../../../shared/services/workspace.service';

@Component({
  selector: 'mtm-summary-block-simplified',
  templateUrl: './summary-block-simplified.component.html',
  styleUrl: './summary-block-simplified.component.scss',
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
  standalone: false,
})
export class SummaryBlockSimplifiedComponent implements OnInit, OnDestroy {
  _timelineData: any;

  get timelineData() {
    return this._timelineData;
  }
  @Input()
  set timelineData(value: any) {
    this._timelineData = value;
    this.updateFormattedDate();
  }
  @Input() isDownloadComponentActive: boolean = false;
  workspaceName: string;

  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly featureConfigService = inject(FeatureConfigService);
  private readonly projectV2Service = inject(ProjectV2ServiceService);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly ngUnsubscribe = new Subject<void>();
  protected campaignMainConfig: CampaignMainFeatureConfig;
  protected formattedDate: NgbDate | null = null;
  protected companyUsers: User[] = [];
  protected companyId: string;

  ngOnInit() {
    const authUser = this.authService.getAuthUser();
    this.companyId = authUser.companyId;
    this.workspaceName = this.projectV2Service.workspaceName;

    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_MAIN])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.campaignMainConfig = response[FeatureTypes.FEA_CAMPAIGN_MAIN];
        },
      });
    this.loadCompanyUsers();
    if (!this.workspaceName) {
      this.getWorkspaceInfo();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  loadCompanyUsers() {
    this.userService
      .getUsersByCompanyId(this.companyId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.companyUsers = data;
      });
  }

  private updateFormattedDate() {
    try {
      const date = new Date(this.timelineData.expectedMad);
      const formattedDate = {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate(),
      };
      this.formattedDate = NgbDate.from(formattedDate);
    } catch (err) {
      this.formattedDate = null;
    }
  }

  getUserInfo(email: string): string {
    const user = this.companyUsers?.find((user) => user.username === email);
    return user ? user.fullName : email;
  }

  getWorkspaceInfo() {
    const { workspaceId } = this.timelineData;
    this.workspaceService
      .getWorkspaceById(this.companyId, workspaceId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (workspace) => {
          if (workspace) {
            this.workspaceName = workspace.name;
          }
        },
        error: (error) => {
          console.error('Error fetching workspace info:', error);
        },
      });
  }
}
